import React, { useState, useEffect } from 'react';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import './LogModals.css';

interface Animal {
  id: number;
  name: string;
  category: string;
  count: number;
}

interface EggLogModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const EggLogModal: React.FC<EggLogModalProps> = ({ onClose, onSuccess }) => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [selectedAnimal, setSelectedAnimal] = useState<number | null>(null);
  const [eggCount, setEggCount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ points: number; message: string } | null>(null);

  useEffect(() => {
    loadAnimals();
  }, []);

  const loadAnimals = async () => {
    try {
      const data = await bridgeClubAPI.getAnimals();
      // Filter to poultry only
      const poultry = data.filter((a: Animal) => a.category === 'poultry');
      setAnimals(poultry);
      // Auto-select if only one flock
      if (poultry.length === 1) {
        setSelectedAnimal(poultry[0].id);
      }
    } catch (err) {
      setError('Failed to load animals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAnimal || !eggCount) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await bridgeClubAPI.logEggsForPoints({
        animal_id: selectedAnimal,
        egg_count: parseInt(eggCount),
      });

      if (response.success) {
        setSuccess({
          points: response.points_earned,
          message: response.message,
        });
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to log eggs');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to log eggs');
    } finally {
      setSubmitting(false);
    }
  };

  const quickCounts = [6, 12, 18, 24];

  return (
    <div className="log-modal-overlay" onClick={onClose}>
      <div className="log-modal" onClick={e => e.stopPropagation()}>
        <div className="log-modal-header">
          <span className="log-modal-icon">🥚</span>
          <h2>Log Eggs</h2>
          <button className="log-modal-close" onClick={onClose}>&times;</button>
        </div>

        {success ? (
          <div className="log-success">
            <span className="success-icon">✅</span>
            <p className="success-points">+{success.points} points!</p>
            <p className="success-message">{success.message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {loading ? (
              <div className="log-loading">Loading...</div>
            ) : animals.length === 0 ? (
              <div className="log-empty">
                <p>No poultry found.</p>
                <p className="log-empty-hint">
                  Add chickens, ducks, or other poultry to log eggs.
                </p>
              </div>
            ) : (
              <>
                {animals.length > 1 && (
                  <div className="form-group">
                    <label>Select Flock</label>
                    <div className="animal-select-grid">
                      {animals.map(animal => (
                        <button
                          key={animal.id}
                          type="button"
                          className={`animal-select-btn ${selectedAnimal === animal.id ? 'selected' : ''}`}
                          onClick={() => setSelectedAnimal(animal.id)}
                        >
                          <span className="animal-select-icon">🐔</span>
                          <span className="animal-select-name">{animal.name || `Flock #${animal.id}`}</span>
                          <span className="animal-select-weight">{animal.count} birds</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="eggCount">Eggs Collected Today</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="eggCount"
                    value={eggCount}
                    onChange={e => setEggCount(e.target.value)}
                    onBlur={e => {
                      const val = parseInt(e.target.value) || 0;
                      if (val > 0) setEggCount(String(val));
                      else setEggCount('');
                    }}
                    placeholder="How many eggs?"
                    required
                  />
                  <div className="quick-buttons">
                    {quickCounts.map(count => (
                      <button
                        key={count}
                        type="button"
                        className="quick-btn"
                        onClick={() => setEggCount(count.toString())}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>

                {error && <div className="log-error">{error}</div>}

                <div className="log-modal-actions">
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={!selectedAnimal || !eggCount || submitting}
                  >
                    {submitting ? 'Logging...' : 'Log Eggs'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default EggLogModal;
